import { Fragment, Slice, type ResolvedPos } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';

import { TiptapNodeType } from '../../tiptapTypes';

const LIST_SELECTOR = 'ul, ol';
const HEADING_SELECTOR = 'h1, h2, h3, h4, h5, h6';
const BLOCK_SELECTOR = `p, div, blockquote, pre, table, li, ${LIST_SELECTOR}, ${HEADING_SELECTOR}`;
const SKIPPED_SELECTOR = 'meta, style, script';

/** A list of lines is open through the list, its item and the item's paragraph at both ends. */
const TASK_LINE_DEPTH = 3;

/** A pasted paragraph is open through its text at both ends, as ProseMirror itself builds it. */
const PARAGRAPH_DEPTH = 1;

const isElement = (node: Node): node is Element => node.nodeType === Node.ELEMENT_NODE;
const hasText = (node: Node): boolean => !!node.textContent?.trim();
const holdsBlocks = (element: Element): boolean =>
    Array.from(element.children).some((child) => child.matches(BLOCK_SELECTOR));

/**
 * Checks whether the HTML was copied from a task list editor. Such a copy pastes as it is, with
 * the nesting ProseMirror restores from the context it recorded.
 */
const isTaskListCopy = (body: Element): boolean => {
    const sliceContext = body.querySelector('[data-pm-slice]')?.getAttribute('data-pm-slice') ?? '';

    return sliceContext.includes(TiptapNodeType.taskList) || sliceContext.includes(TiptapNodeType.taskItem);
};

/**
 * Replaces each heading with a paragraph holding its content, as a task holds no heading. A
 * copied list's title is one.
 */
const replaceHeadings = (document: Document, body: Element): void => {
    body.querySelectorAll(HEADING_SELECTOR).forEach((heading) => {
        const paragraph = document.createElement('p');

        paragraph.append(...Array.from(heading.childNodes));
        heading.replaceWith(paragraph);
    });
};

/**
 * Wraps a node in a task item, in the node's place when it has one.
 */
const wrapInTaskItem = (document: Document, node: ChildNode): Element => {
    const item = document.createElement('li');

    item.setAttribute('data-type', 'taskItem');
    node.replaceWith(item);
    item.appendChild(node);

    return item;
};

/**
 * Marks every list as a task list and every item as a task item, keeping their nesting.
 *
 * Some apps write a nested list straight inside the outer list, beside the item it belongs
 * under. That list moves into the item before it.
 */
const toTaskLists = (document: Document, body: Element): void => {
    body.querySelectorAll(LIST_SELECTOR).forEach((list) => {
        list.setAttribute('data-type', 'taskList');

        Array.from(list.children).forEach((child) => {
            if (child.matches('li')) return;

            const previousItem = child.previousElementSibling;

            if (child.matches(LIST_SELECTOR) && previousItem?.matches('li')) {
                previousItem.appendChild(child);
                return;
            }

            wrapInTaskItem(document, child);
        });
    });

    body.querySelectorAll('li').forEach((item) => item.setAttribute('data-type', 'taskItem'));
};

/**
 * Collects the pasted HTML's lines and lists in order. Looks through wrappers that hold blocks,
 * such as the one Google Docs puts around a copy. Bare text becomes a paragraph.
 */
const collectBlocks = (document: Document, parent: Node): Element[] =>
    Array.from(parent.childNodes).flatMap((node) => {
        if (!hasText(node)) return [];

        if (!isElement(node)) {
            const paragraph = document.createElement('p');

            paragraph.appendChild(node);

            return [paragraph];
        }

        if (node.matches(SKIPPED_SELECTOR)) return [];
        if (node.matches(LIST_SELECTOR) || !holdsBlocks(node)) return [node];

        return collectBlocks(document, node);
    });

/**
 * Rewrites pasted HTML as one task list. Each loose block becomes an item, a list's items join at
 * the same level, and the nesting inside a list is kept.
 */
const toTaskListHtml = (html: string): string => {
    const document = new DOMParser().parseFromString(html, 'text/html');
    const { body } = document;

    if (isTaskListCopy(body)) return html;

    // A copy from another editor is rebuilt like any other HTML
    body.querySelector('[data-pm-slice]')?.removeAttribute('data-pm-slice');
    replaceHeadings(document, body);
    toTaskLists(document, body);

    const blocks = collectBlocks(document, body);
    const isSingleLine = blocks.length === 1 && !blocks[0].matches(LIST_SELECTOR);

    // A single line pastes inline as it is
    if (!blocks.length || isSingleLine) return html;

    const taskList = document.createElement('ul');

    taskList.setAttribute('data-type', 'taskList');

    blocks.forEach((block) => {
        if (block.matches(LIST_SELECTOR)) {
            taskList.append(...Array.from(block.children));
            return;
        }

        const item = wrapInTaskItem(document, block);

        taskList.appendChild(item);
    });

    return taskList.outerHTML;
};

/**
 * Builds the slice for pasted plain text. Lines pasted into a task become one task each. A single
 * line, or lines pasted into the title, stay paragraphs, which is what ProseMirror builds itself.
 */
const parseTextLines = (text: string, $context: ResolvedPos): Slice => {
    const { schema } = $context.doc.type;
    const marks = $context.marks();
    const paragraphs = text
        .split(/(?:\r\n?|\n)+/)
        .map((line) => schema.nodes[TiptapNodeType.paragraph].create(null, line ? schema.text(line, marks) : null));
    const isCaretInTitle = $context.parent.type.name === TiptapNodeType.taskListTitle;

    if (paragraphs.length < 2 || isCaretInTitle) {
        return new Slice(Fragment.from(paragraphs), PARAGRAPH_DEPTH, PARAGRAPH_DEPTH);
    }

    const taskItems = paragraphs.map((paragraph) => schema.nodes[TiptapNodeType.taskItem].create(null, paragraph));
    const taskList = schema.nodes[TiptapNodeType.taskList].create(null, taskItems);

    return new Slice(Fragment.from(taskList), TASK_LINE_DEPTH, TASK_LINE_DEPTH);
};

/**
 * Pastes lines and bullets as tasks. Lines at one level become sibling tasks, and bullets nested
 * in the source become sub-tasks.
 *
 * The schema holds a task to one paragraph. When loose paragraphs are parsed at the caret,
 * ProseMirror wraps each one after the first in a nested list to make it fit, and the lines land
 * as sub-tasks of the first. The HTML is rewritten as one task list before it is parsed, which
 * leaves ProseMirror nothing to wrap. Plain text is built as a list directly.
 *
 * A paste into the title keeps ProseMirror's own handling, as a list can't be fitted there.
 */
export const createTaskListItemPastedLinesPlugin = (): Plugin =>
    new Plugin({
        key: new PluginKey('taskListItemPastedLines'),
        props: {
            transformPastedHTML: (html, view) => {
                const isCaretInTitle = view.state.selection.$from.parent.type.name === TiptapNodeType.taskListTitle;

                return isCaretInTitle ? html : toTaskListHtml(html);
            },
            clipboardTextParser: (text, $context) => parseTextLines(text, $context),
        },
    });
